import { Box } from "@chakra-ui/layout";
import {useDragScrolling, usePack} from "lib/hooks";
import { ItemLevels } from "lib/hooks/usePackAssets";
import { FC, useRef, useEffect, useMemo } from "react";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { DnDAssetProvider, useDnd } from "./context";
import { Asset } from "domains/single-asset";
import { useBreakpointValue } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { isPreviewingPack, isReadOnly } from "lib/utils";
import { usePackContext } from "domains/pack";

type Props = {
  data: any;
  path: string;
  selectedAssets: any[];
}

export const SingleAsset: FC<Props> = ({ data, path, selectedAssets }) => {
  const ref = useRef(null);
  const { addEventListenerForWindow, removeEventListenerForWindow } = useDragScrolling();
  const { setIsDragInProgress } = useDnd();
  const isMobile = useBreakpointValue([true, false]);
  const { packId } = usePackContext();
  const { data: pack } = usePack(packId);
  const { pathname } = useRouter();

  const isPreviewing = isPreviewingPack(pathname);

  const isSelected = useMemo(() => {
    const matched = selectedAssets.find((selected) => {
      const splitPath = selected.split('-');

      if (splitPath.length === 1) {
        return path.startsWith(selected);
      }

      return selected === path;
    });

    return !!matched;
  }, [ path, selectedAssets ]);

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemLevels.ASSET,
    item: () => {
      addEventListenerForWindow();
      return {
        id: data.id,
        path,
        level: ItemLevels.ASSET,
        type: data.type,
        selectedAssets,
      }
    },
    canDrag: () => {
      if (isMobile) {
        return isSelected && !isReadOnly(pack) && !isPreviewing;
      }
      return !isPreviewing && !isReadOnly(pack)
    },
    end: () => {
      removeEventListenerForWindow();
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging()
    })
  }, [ data, path, selectedAssets, isSelected, isMobile ]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true })
  }, [ preview ]);

  useEffect(() => {
    setIsDragInProgress(isDragging);
  }, [ setIsDragInProgress, isDragging ])

  const opacity = isDragging ? 0.3 : 1;

  drag(ref);

  return (
    <Box ref={ref}>
      <DnDAssetProvider value={{ opacity, isSelected, isDragging, asset: data, path }}>
        <Asset type={data.type} />
      </DnDAssetProvider>
    </Box>
  )
}
